import { Breadcrumb, Checkbox, IBreadcrumbItem, SearchBox, Spinner, SpinnerSize, Stack, Text } from '@fluentui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridType } from '../../../../../Entities/GridType';
import { ResourceType } from '../../../../../Entities/ResourceType';
import EnumEx from '../../../../../Utilities/EnumEx';
import { ApiCalls } from '../../../../../api/api';
import { ResourceListItem, RootSettings, UserType } from '../../../../../api/generated/data-contracts';
import { useUISettings } from '../../../../../context/network/http/QueryProvider/queries/UISettings';
import { useStore } from '../../../../../context/store';
import { functionThatReturnsTrue } from '../../../../../helpers/functionPrimitives';
import { useControlledState } from '../../../../../hooks/useControlledState';
import { useTPPermissions } from '../../../../../hooks/usePermissions';
import { FilterList } from '../components/FilterList/FilterList';
import { ListItemShimmer } from '../components/ListItemShimmer';
import { buildRbsTree } from '../helpers/buildRbsTree';
import { BreadcrumbGroupTitle, TFilter } from '../types';
import { ResultList } from './components/ResultList/ResultList';
import { makeResourceFilterDefaults, resolveResourceFieldName } from './helpers';
import { useCurrentResourceCategory } from './hooks/useCurrentResourceCategory';
import { useSearchedFilteredData } from './hooks/useSearchedFilteredData';
import { useTranslation } from '../../../../../Locales/i18n/localizationService';

type ResourcePickerProps = {
    setRequestNamedResources: React.Dispatch<React.SetStateAction<boolean>>;
    requestNamedResources: boolean;
    isFetching: boolean;
    setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
    data: ResourceListItem[];
    queryKey: readonly [string, any];
    showAll: boolean;
    setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
    gridType: GridType;
    isFetched: boolean;
};

export const ResourcePicker: React.FC<ResourcePickerProps> = ({
    setIsDragging,
    data,
    isFetching,
    requestNamedResources,
    setRequestNamedResources,
    queryKey,
    setShowAll,
    showAll,
    gridType,
    isFetched,
}) => {
    const { t } = useTranslation();
    const { data: uiSettings } = useUISettings();
    const delegationMode = useStore(store => store.ui.delegationMode);
    const isSuperuser = useTPPermissions({ userTypes: [UserType.SuperUser] });

    const panelGroupings = useMemo(() => {
        if (EnumEx.equalsAny(gridType, GridType.Allocation, GridType.LmCapacityProjects, GridType.ResourceCapacity)) {
            // return uiSettings.settings.resourcePanelAllocationGroupings || [];

            // Custom properties might exist and might not. Make sure we only work with properties that actually exists
            return (
                uiSettings.settings.resourcePanelAllocationGroupings?.filter(grouping => Boolean(resolveResourceFieldName(grouping, uiSettings.settings))) || []
            );
        }
        if (EnumEx.equalsAny(gridType, GridType.Request, GridType.WorkPackage)) {
            // return uiSettings.settings.resourcePanelRequestGroupings || [];

            // Custom properties might exist and might not. Make sure we only work with properties that actually exists
            return (
                uiSettings.settings.resourcePanelRequestGroupings?.filter(grouping => Boolean(resolveResourceFieldName(grouping, uiSettings.settings))) || []
            );
        }
        return [];
        // }, [gridType, uiSettings.settings.resourcePanelAllocationGroupings, uiSettings.settings.resourcePanelRequestGroupings]);
    }, [gridType, uiSettings.settings]);

    const [filters, setFilters] = useState<TFilter[]>(() => makeResourceFilterDefaults(panelGroupings));
    const [breadcrumbTitles, setBreadcrumbTitles] = useControlledState(
        () => makeBreadcrumbDefaultTitles(panelGroupings, uiSettings.settings),
        [panelGroupings, uiSettings.settings],
    );

    const titles = useMemo(
        () =>
            panelGroupings.map(grouping => {
                return resolveResourceFieldName(grouping, uiSettings.settings) || (grouping === 'rbs' ? 'RBS' : grouping);
            }),
        [panelGroupings, uiSettings.settings],
    );

    const [currentFilterLevel, setCurrentFilterLevel] = useState(0);

    const currentTitle = titles[currentFilterLevel - 1];

    const queryClient = useQueryClient();
    const skipNextReset = useRef(false);
    const updateItem = useCallback(
        (updatedResource: ResourceListItem) => {
            skipNextReset.current = true;
            queryClient.setQueryData<ResourceListItem[]>(queryKey, resources =>
                resources.map(resource => (resource.id === updatedResource.id ? updatedResource : resource)),
            );
        },
        [queryClient, queryKey],
    );

    const filteredData = useMemo(() => {
        return filters.slice(0, currentFilterLevel).reduce((acc, { filterFn }) => acc.filter(filterFn), data);
    }, [currentFilterLevel, data, filters]);

    const updateFilterState = useCallback(
        (grouping: string, filterFn: (resource: ResourceListItem) => boolean, breadcrumbText: string, filterDirection: number = 1) => {
            setFilters(filters =>
                filters.map(filter => {
                    if (filter.grouping === grouping) {
                        return { ...filter, filterFn };
                    }
                    return filter;
                }),
            );
            setBreadcrumbTitles(bcTitles =>
                bcTitles.map(bcTitle => {
                    if (bcTitle.grouping === grouping) {
                        return { ...bcTitle, title: breadcrumbText };
                    }
                    return bcTitle;
                }),
            );
            if (filterDirection) {
                setCurrentFilterLevel(level => level + filterDirection);
            }
        },
        [setBreadcrumbTitles],
    );

    const { currentCategory, resetCurrentRBSPath } = useCurrentResourceCategory({
        filterLevel: currentFilterLevel,
        data: filteredData,
        filters,
        settings: uiSettings.settings,
        panelGroupings,
        breadcrumbTitles,
        updateFilterState,
    });

    const breadcrumbItems = useMemo(() => {
        return [
            {
                key: 'root',
                text: '/',
                onClick: () => {
                    setCurrentFilterLevel(1);
                    setFilters(makeResourceFilterDefaults(panelGroupings));
                    resetCurrentRBSPath();
                    setBreadcrumbTitles(makeBreadcrumbDefaultTitles(panelGroupings, uiSettings.settings));
                },
            },
            ...filters.slice(0, currentFilterLevel - 1).map((_, slicedFilterIndex) => {
                const { title } = breadcrumbTitles[slicedFilterIndex];
                return {
                    key: title + slicedFilterIndex,
                    text: title,
                    onClick: () => {
                        setCurrentFilterLevel(slicedFilterIndex + 1);
                        setFilters(filters => {
                            return filters.map((filter, filterIndex) => {
                                if (filterIndex >= slicedFilterIndex) {
                                    return {
                                        ...filter,
                                        filterFn: functionThatReturnsTrue,
                                    };
                                }
                                return filter;
                            });
                        });
                    },
                };
            }),
        ] as IBreadcrumbItem[];
    }, [filters, currentFilterLevel, panelGroupings, resetCurrentRBSPath, setBreadcrumbTitles, uiSettings.settings, breadcrumbTitles]);

    useEffect(() => {
        if (!skipNextReset.current) {
            setFilters(makeResourceFilterDefaults(panelGroupings));
            setCurrentFilterLevel(1);
            resetCurrentRBSPath();
        }
        skipNextReset.current = false;
    }, [uiSettings.settings, panelGroupings, gridType, resetCurrentRBSPath]);

    const [searchGenericResourcesByName, setSearchGenericResourcesByName] = useState(false);

    const queryKeyForNamedResources = useMemo(() => {
        const [keyString, keyObject] = queryKey;
        return [
            keyString,
            'named-resources',
            { ...keyObject, selectAll: false, resourceTypes: [ResourceType.Named], matchRbs: false, delegationMode: false, genericResources: null },
        ] as const;
    }, [queryKey]);

    const { data: namedResources, isFetching: fetchingNamedResources } = useQuery({
        queryKey: queryKeyForNamedResources,

        queryFn: async ({ queryKey: [string, string2, reqObject] }) => {
            try {
                return await ApiCalls.getResourcesForPanel(reqObject).then(res => res.data);
            } catch (error) {
                // The error is already handled before this
                return [];
            }
        },
        placeholderData: useRef([] as ResourceListItem[]).current,
        enabled: searchGenericResourcesByName,
    });

    const [searchFilteredData, searchOnChange, searchText] = useSearchedFilteredData({
        data: filteredData,
        namedResources,
        gridType,
        settings: uiSettings.settings,
        searchGenericResourcesByName,
        delegationMode,
    });

    const handleSearchAllClick = useCallback(
        (_: never, checked?: boolean) => {
            if (checked) {
                setSearchGenericResourcesByName(false);
            }
            // setShowAll(checked)
            setRequestNamedResources(checked);
        },
        [setRequestNamedResources],
    );

    const handleSearchGenericResourceClick = useCallback(
        (_: never, checked?: boolean) => {
            if (checked) {
                // setShowAll(false)
                setRequestNamedResources(false);
            }
            setSearchGenericResourcesByName(checked);
        },
        [setRequestNamedResources],
    );

    const rbsTree = useMemo(() => {
        return buildRbsTree(data);
    }, [data]);

    const shouldRenderCheckboxes = useMemo(() => {
        return (
            EnumEx.equalsAny(gridType, GridType.Allocation, GridType.LmCapacityProjects, GridType.ResourceCapacity) &&
            (isSuperuser || uiSettings.settings.allowSearchAllResources) &&
            !delegationMode
        );
    }, [gridType, isSuperuser, uiSettings.settings.allowSearchAllResources, delegationMode]);

    const shouldRenderRequestCheckbox = useMemo(
        () => uiSettings.settings.enableRequestNamedResources && gridType === GridType.Request && !delegationMode,
        [gridType, uiSettings.settings.enableRequestNamedResources, delegationMode],
    );

    const shouldRenderSearchCheckbox = useMemo(
        () => uiSettings.settings.enableSearchNamedResources && gridType === GridType.Request && !delegationMode,
        [gridType, uiSettings.settings.enableSearchNamedResources, delegationMode],
    );

    const checkboxConfigs = useMemo(
        () => [
            {
                condition: shouldRenderCheckboxes,
                label: t('Resources.SearchAllResources'),
                checked: showAll,
                onChange: (e, checked) => setShowAll(checked),
            },
            {
                condition: shouldRenderRequestCheckbox,
                label: t('Resources.RequestBy'),
                checked: requestNamedResources,
                onChange: handleSearchAllClick,
            },
            {
                condition: shouldRenderSearchCheckbox,
                label: t('Resources.SearchByNamed'),
                checked: searchGenericResourcesByName,
                onChange: handleSearchGenericResourceClick,
            },
        ],
        [
            shouldRenderCheckboxes,
            shouldRenderRequestCheckbox,
            shouldRenderSearchCheckbox,
            showAll,
            setShowAll,
            requestNamedResources,
            handleSearchAllClick,
            searchGenericResourcesByName,
            handleSearchGenericResourceClick,
            t,
        ],
    );

    const shouldRenderSpinner = useMemo(() => isFetching || fetchingNamedResources, [isFetching, fetchingNamedResources]);

    const shouldRenderCheckboxArea = useMemo(
        () => checkboxConfigs.some(({ condition }) => condition) || shouldRenderSpinner,
        [checkboxConfigs, shouldRenderSpinner],
    );

    return (
        <Stack styles={{ root: { height: '100%' } }} tokens={{ childrenGap: 10 }}>
            <SearchBox className="tp-panel-searchbox tp-searcher" onChange={searchOnChange} autoFocus />
            {shouldRenderCheckboxArea && (
                <Stack horizontal horizontalAlign="space-between" verticalAlign="start" tokens={{ padding: '0 24px 0 0' }}>
                    <Stack styles={{ root: { minHeight: 35 } }}>
                        {checkboxConfigs.map(({ condition, label, checked, onChange }) => {
                            if (!condition) return null;
                            return (
                                <Checkbox
                                    key={label} // Using `label` as key here, ensure it's unique or use another unique identifier
                                    className="tp-list-checkbox"
                                    label={label}
                                    checked={checked}
                                    onChange={onChange}
                                    disabled={isFetching || fetchingNamedResources}
                                />
                            );
                        })}
                    </Stack>
                    {shouldRenderSpinner && <Spinner size={SpinnerSize.large} labelPosition="right" label="Retrieving data.." />}
                </Stack>
            )}
            <Stack>
                {Boolean(panelGroupings.length) && (
                    <>
                        <Breadcrumb
                            styles={{ root: { '.ms-Breadcrumb-listItem:last-child .ms-Breadcrumb-itemLink': { fontWeight: 'inherit' }, margin: 0 } }}
                            items={breadcrumbItems}
                        />
                        <Text variant="xLarge">{currentTitle || (searchFilteredData.length === 1 ? 'Result' : 'Results')} &nbsp;</Text>
                        <FilterList
                            // gridType as key ensures that the filter resets when the view changes
                            key={gridType}
                            category={currentCategory}
                            rbsTree={rbsTree}
                            setCurrentFilterLevel={setCurrentFilterLevel}
                        />
                    </>
                )}

                <Text>
                    {searchFilteredData.length} {searchFilteredData.length === 1 ? 'result' : 'results'}
                </Text>
            </Stack>
            {!isFetched && (
                <>
                    <ListItemShimmer />
                    <ListItemShimmer opacity={0.5} />
                </>
            )}
            <ResultList
                items={searchFilteredData}
                delegationMode={delegationMode}
                gridType={gridType}
                settings={uiSettings.settings}
                showAll={showAll}
                updateItem={updateItem}
                setIsDragging={setIsDragging}
                searchText={searchText}
            />
        </Stack>
    );
};

const makeBreadcrumbDefaultTitles = (panelGroupings: string[], settings: RootSettings) =>
    panelGroupings.map(grouping => {
        return {
            title: resolveResourceFieldName(grouping, settings) || (grouping === 'rbs' ? 'RBS' : grouping),
            grouping,
        } as BreadcrumbGroupTitle;
    });
